import { useEffect, useState } from "react";
import { InputAdornment, makeStyles } from "@material-ui/core";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Alert from "@material-ui/lab/Alert";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { InputField } from "../../../../../components/input-field/InputField";
import { InputButton } from "../../../../../components/input-button/InputButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";

import { Autocomplete } from "@material-ui/lab";
import BaseAccountConfigurationService from "../../../../../services/BaseAccountConfigurationService";
import { InputDate } from "../../../../../components/input-date/InputDate";
import moment from "moment";
import { CalendarToday } from "@material-ui/icons";

interface AddBaseAccountConfProps {
  currencies: any[];
  countries: any[];
  pricingGroups: any[];
  onSubmitCallback: () => void;
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalTitle: {
    fontSize: 25,
    fontWeight: "bold",
  },
  modalBody: {
    //width: 500,
    padding: 20,
    border: "none",
    overflow: "auto",
  },
  addBox: {
    padding: 5,
  },
  creditBaseField: {
    height: 40,
  },
  searchAuto: {
    //border: '1px outset lightgray',
  },

  antPickerDropdown: {
    zIndex: 100000,
  },
}));

export const AddBaseAccountConf = (props: AddBaseAccountConfProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const { formatMessage } = useIntl();
  //const { branchId, isOpened, closeCallback } = props;
  const { pricingGroups, onSubmitCallback } = props;
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [succeed, setSucceed] = useState(false);
  const [currencies, setCurrencies] = useState(props.currencies);
  const [countries, setCountries] = useState(props.countries);

  const formik = useFormik({
    initialValues: {
      credit_base: "",
      assignment_date: "",
      pricing_group_id: "",
      currency_id: "",
      country_id: "",
    },
    validationSchema: Yup.object().shape({
      credit_base: Yup.number().required(
        formatMessage({ id: "sharedScreen.pleaseEnterCreditBase" })
      ),
      assignment_date: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseEnterAssignmentDate" })
      ),
      pricing_group_id: Yup.number().required(
        formatMessage({ id: "sharedScreen.pleaseEnterPricingGroup" })
      ),
      currency_id: Yup.number().required(
        formatMessage({ id: "sharedScreen.pleaseEnterCurrency" })
      ),
      country_id: Yup.number().required(
        formatMessage({ id: "sharedScreen.pleaseEnterCountry" })
      ),
    }),
    onSubmit: async (
      values,
      { setSubmitting, resetForm }: FormikHelpers<any>
    ) => {
      setError(false);
      try {
        const addBaseAccountResult = await BaseAccountConfigurationService.addBaseAccountConf(
          formik.values
        );
        if (
          addBaseAccountResult.isSuccess &&
          addBaseAccountResult.data.code === 100
        ) {
          resetForm({});
          setSucceed(true);
          setTimeout(() => {
            setSucceed(false);
          }, 2000);
          onSubmitCallback();
        } else {
          setError(true);
          if (addBaseAccountResult.data.code === 101) {
            setErrorMessage("Similar base account configuration already exist");
          } else {
            setErrorMessage("");
          }
        }
      } catch (error) {
        setError(true);
      }
      setSubmitting(false);
    },
  });

  const handleDateChange = (name: string, dateString: string) => {
    formik.setValues({ ...formik.values, [name]: dateString });
  };

  return (
    <>
      <Paper className={classes.modalBody}>
        <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          <Grid container spacing={2}>
            <Grid
              item
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              xs={12}
              sm={12}
              lg={12}
            >
              <Typography
                className={classes.modalTitle}
                variant="h4"
                component="h4"
              >
                <FormattedMessage id="sharedScreen.addBaseAccountConfiguration" />
              </Typography>
            </Grid>
            {error && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="error">
                  {errorMessage === "" && (
                    <FormattedMessage id="sharedScreen.thereWasAProblemCreatingBaseAccountConfig" />
                  )}
                  {errorMessage !== "" && <p>{errorMessage}</p>}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={12} lg={12}>
              <Grid item xs={12} sm={12} lg={12} className={classes.addBox}>
                <InputField
                  className={classes.creditBaseField}
                  value={formik.values.credit_base}
                  type="number"
                  fullWidth
                  label={formatMessage({
                    id: "sharedScreen.creditBase",
                  })}
                  placeholder={formatMessage({
                    id: "sharedScreen.creditBase",
                  })}
                  name="credit_base"
                  onChange={formik.handleChange}
                  error={
                    formik.touched.credit_base &&
                    Boolean(formik.errors.credit_base)
                  }
                  helperText={
                    formik.touched.credit_base && formik.errors.credit_base
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} lg={12} className={classes.addBox}>
                <InputDate
                  minDate={moment()
                    .subtract("year", 2)
                    .format("YYYY-MM-DD HH:mm:ss")}
                  onDateChange={(name, date) => handleDateChange(name, date)}
                  inputProps={{
                    disabled: formik.isSubmitting,
                    fullWidth: true,
                    id: "assignment_date",
                    style: { height: 40 },
                    name: "assignment_date",
                    label: intl.formatMessage({
                      id: "sharedScreen.assignmentDate",
                    }),
                    placeholder: intl.formatMessage({
                      id: "sharedScreen.assignmentDate",
                    }),
                    endAdornment: (
                      <InputAdornment position="start">
                        <CalendarToday />
                      </InputAdornment>
                    ),
                    value: formik.values.assignment_date,
                    error:
                      Boolean(formik.touched.assignment_date) &&
                      Boolean(formik.errors.assignment_date),
                    helperText:
                      formik.touched.assignment_date &&
                      formik.errors.assignment_date,
                  }}
                  pickerProps={{
                    format: "YYYY-MM-DD HH:mm:ss",
                    disabled: false,
                    showToday: false,
                    showTime: true,
                  }}
                />
              </Grid>
              {/*<Grid item xs={12} sm={12} lg={12} className={classes.addBox}>
                <InputField
                  value={formik.values.assignment_date}
                  type="date"
                  fullWidth
                  label={formatMessage({
                    id: "sharedScreen.assignmentDate",
                  })}
                  placeholder={formatMessage({
                    id: "sharedScreen.assignmentDate",
                  })}
                  name="assignment_date"
                  onChange={formik.handleChange}
                  error={
                    formik.touched.assignment_date && Boolean(formik.errors.assignment_date)
                  }
                  helperText={
                    formik.touched.assignment_date && formik.errors.assignment_date
                  }
                />
                </Grid>*/}
              <Grid item xs={12} sm={12} lg={12} className={classes.addBox}>
                <Autocomplete
                  className={classes.searchAuto}
                  id="pricingGroups"
                  options={pricingGroups}
                  renderInput={(param) => (
                    <TextField
                      {...param}
                      label={formatMessage({
                        id: "sharedScreen.pricingGroupId",
                      })}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  getOptionLabel={(Option) => Option.code}
                  //onChange={formik.handleChange}
                  onChange={(event, pricingG) => {
                    {
                      pricingG != null &&
                        formik.setFieldValue("pricing_group_id", pricingG.id);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} lg={12} className={classes.addBox}>
                <Autocomplete
                  className={classes.searchAuto}
                  id="currencies"
                  options={currencies}
                  renderInput={(param) => (
                    <TextField
                      {...param}
                      label={formatMessage({
                        id: "sharedScreen.currencyId",
                      })}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  getOptionLabel={(Option) => Option.code}
                  onChange={(event, currency) => {
                    {
                      currency != null &&
                        formik.setFieldValue("currency_id", currency.id);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} lg={12} className={classes.addBox}>
                <Autocomplete
                  id="countries"
                  options={countries}
                  renderInput={(param) => (
                    <TextField
                      {...param}
                      label={formatMessage({
                        id: "sharedScreen.countryId",
                      })}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  getOptionLabel={(Option) => Option.designation}
                  //onChange={formik.handleChange}
                  onChange={(event, country) => {
                    {
                      country != null &&
                        formik.setFieldValue("country_id", country.id);
                    }
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                lg={12}
                container
                direction="row"
                className={classes.addBox}
              >
                <InputButton
                  disabled={formik.isSubmitting}
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="secondary"
                >
                  <FormattedMessage id="sharedScreen.create" />
                  {formik.isSubmitting && <CircularProgress size={30} />}
                </InputButton>
              </Grid>
              {succeed && (
                <Grid item xs={12} sm={12} lg={12}>
                  <Alert severity="success">
                    <FormattedMessage id="sharedScreen.BaseAccountConfigurationCreatedSuccessfully" />
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </>
  );
};
